// src/hooks/useProjects.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useStableAuth } from '@/hooks/useStableAuth';
import { toast } from 'sonner';
import type { Project, CreateProjectInput } from '@/types/project';

// ─── Query keys ───────────────────────────────────────────────────────────────

export const projectKeys = {
  all: ['projects'] as const,
  lists: () => [...projectKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) =>
    [...projectKeys.lists(), filters] as const,
  detail: (id: string) => [...projectKeys.all, 'detail', id] as const,
};

// ─── Hook principal ────────────────────────────────────────────────────────────

export function useProjects(showArchived = false) {
  const { userId, companyId } = useStableAuth();
  const queryClient = useQueryClient();

  // ── Invalidação global ─────────────────────────────────────────────────────
  const invalidateAllProjects = () => {
    queryClient.invalidateQueries({ queryKey: projectKeys.all });
  };

  // ── Listagem ───────────────────────────────────────────────────────────────
  const projectsQuery = useQuery({
    queryKey: projectKeys.list({ userId, showArchived }),
    queryFn: async (): Promise<Project[]> => {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          id,
          company_id,
          name,
          description,
          color,
          icon,
          is_archived,
          created_by,
          created_at,
          updated_at,
          project_members(role, user_id)
        `)
        .eq('is_archived', showArchived)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Extrai o papel do usuário atual em cada projeto
      return (data ?? []).map((row: any) => {
        const myMembership = row.project_members?.find(
          (m: any) => m.user_id === userId
        );
        return {
          id: row.id,
          company_id: row.company_id,
          name: row.name,
          description: row.description,
          color: row.color,
          icon: row.icon,
          is_archived: row.is_archived,
          created_by: row.created_by,
          created_at: row.created_at,
          updated_at: row.updated_at,
          member_count: row.project_members?.length ?? 0,
          my_role: myMembership?.role ?? null,
        };
      });
    },
    enabled: !!userId && !!companyId,
  });

  // ── Criação ────────────────────────────────────────────────────────────────
  const createProjectMutation = useMutation({
    mutationFn: async (input: CreateProjectInput): Promise<string> => {
      const { data, error } = await supabase.rpc('create_project_atomic', {
        p_name: input.name,
        p_description: input.description ?? null,
        p_color: input.color ?? null,
        p_icon: input.icon ?? null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      invalidateAllProjects();
      queryClient.refetchQueries({ queryKey: projectKeys.lists() });
      toast.success('Projeto criado com sucesso!');
    },
    onError: (error: Error) => {
      console.error('Erro ao criar projeto:', error);
      toast.error('Erro ao criar projeto. Tente novamente.');
    },
  });

  // ── Arquivar ───────────────────────────────────────────────────────────────
  const archiveProjectMutation = useMutation({
    mutationFn: async (projectId: string): Promise<void> => {
      const { error } = await supabase
        .from('projects')
        .update({ is_archived: true, updated_at: new Date().toISOString() })
        .eq('id', projectId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateAllProjects();
      toast.success('Projeto arquivado.');
    },
    onError: (error: Error) => {
      console.error('Erro ao arquivar projeto:', error);
      toast.error('Erro ao arquivar projeto.');
    },
  });

  // ── Excluir ────────────────────────────────────────────────────────────────
  const deleteProjectMutation = useMutation({
    mutationFn: async (projectId: string): Promise<void> => {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Projeto excluído com sucesso');
    },
    onError: (err: any) => {
      toast.error('Erro ao excluir projeto: ' + err.message);
    },
  });

  return {
    projects: projectsQuery.data ?? [],
    isLoading: projectsQuery.isLoading,
    error: projectsQuery.error,
    refetch: projectsQuery.refetch,
    createProject: createProjectMutation.mutateAsync,
    isCreating: createProjectMutation.isPending,
    archiveProject: archiveProjectMutation.mutateAsync,
    isArchiving: archiveProjectMutation.isPending,
    deleteProject: deleteProjectMutation.mutateAsync,
    isDeletingProject: deleteProjectMutation.isPending,
  };
}

// ─── Hook de detalhe de um projeto ────────────────────────────────────────────

export function useProject(projectId: string | undefined) {
  const { userId, companyId } = useStableAuth();

  return useQuery({
    queryKey: projectKeys.detail(projectId ?? ''),
    queryFn: async (): Promise<Project> => {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          id, company_id, name, description, color, icon,
          is_archived, created_by, created_at, updated_at,
          project_members(role, user_id)
        `)
        .eq('id', projectId!)
        .single();

      if (error) throw error;

      const myMembership = (data as any).project_members?.find(
        (m: any) => m.user_id === userId
      );

      return {
        ...(data as any),
        member_count: (data as any).project_members?.length ?? 0,
        my_role: myMembership?.role ?? null,
      };
    },
    enabled: !!projectId && !!userId && !!companyId,
  });
}
